import type { LevelDefinition } from "./types";
import type { RandomSource } from "@/core/rng";

/**
 * Endless Growth V1: a single, permanent, fixed 14x14 open arena - no
 * gate, no target length, unlimited attempts. Never registered in
 * `LevelRegistry`/`WorldRegistry` (same precedent as Daily Challenge
 * levels - see `src/daily/convert.ts`'s doc), so it can never appear in
 * Campaign world/level selection or Campaign progress, whatever `worldId`
 * string it carries.
 */
export const ENDLESS_LEVEL_ID = "endless-v1";

const ENDLESS_GRID = { cols: 14, rows: 14 } as const;

/**
 * Head at the grid's center, body extending left (opposite `startDirection`
 * - see `buildInitialSnakeBody()`), starting length 3, direction right -
 * the exact configuration the product spec calls for. `LevelValidator`
 * confirms this is a legal, fully-connected level (see
 * `endlessLevel.test.ts`).
 */
const ENDLESS_SNAKE_START = { x: 7, y: 7 } as const;

export function buildEndlessLevelDefinition(): LevelDefinition {
  return {
    id: ENDLESS_LEVEL_ID,
    name: "ENDLESS",
    // Never a real Campaign world id (`w1`/`w2`/...) and never added to
    // `WorldRegistry`/`WORLD_1_LEVELS`/`WORLD_2_LEVELS` - this level is
    // simply never enumerated anywhere Campaign UI reads from.
    worldId: "endless",
    levelInWorld: 1,
    order: 0,
    objective: "survival",
    grid: ENDLESS_GRID,
    snakeStart: ENDLESS_SNAKE_START,
    startDirection: "right",
    initialLength: 3,
    // `LevelGame`'s `objective: "survival"` guard makes the gate
    // structurally unreachable at any length, ever (see `checkGateOpen()`)
    // - `targetLength` is only kept because `LevelDefinition`'s shape and
    // `LevelValidator`'s free-space check both require a real number.
    // Same precedent as Daily's V2 survival specs
    // (`src/daily/convert.ts`): trivially satisfied by the starting
    // length, so it is structurally inert, never a real goal.
    targetLength: 3,
    baseStepMs: 165, // Locked: equal to Earth's (world 2) current starting speed.
    speedMode: "endless", // Locked: Endless's own permanent 75ms cap - see `GameConfig.speedEngine.endlessCaps`.
    obstacles: [],
    // No visible gate is ever rendered/reachable for a survival-objective
    // run (see above), so this is a hidden dummy cell in a harmless
    // corner - kept only to satisfy `LevelDefinition`'s required shape and
    // `LevelValidator`'s exit-reachability check. Same precedent as
    // Daily's V2 survival specs.
    exit: { x: 0, y: 0 },
    // Locked: Endless is the only mode that opts into ARENA CLEARED - see
    // `LevelDefinition.completeWhenNoBitesCanSpawn`'s own doc.
    completeWhenNoBitesCanSpawn: true,
  };
}

/**
 * The exact `LevelGame` constructor options every Endless run must use:
 * STARFRUIT permanently disabled via the same proven RNG injection seam
 * Daily already uses (`starfruitRandom` always resolves to "never
 * eligible") - never a new Endless-specific power, never a change to
 * Campaign/Daily's own STARFRUIT behavior, which never passes this option
 * (Campaign) or already disables it the same way (Daily, independently).
 * Bite placement/CHAOS randomness is deliberately left as the real
 * `Math.random` default (unlike Daily's seeded RNG) - Endless has no
 * "same challenge for everyone" requirement, every attempt is its own
 * fresh random run.
 */
export function endlessLevelGameOptions(): { starfruitRandom: RandomSource } {
  return { starfruitRandom: () => 1 };
}
